import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { CalendarIcon, Filter, Sparkles, X, Clock, Calendar, LocateIcon, MapPin } from "lucide-react";
import "react-calendar/dist/Calendar.css";
import { CircleLoader, ClipLoader } from "react-spinners";
import { tamilNaduSubdistricts } from '../utils/tamilNaduSubdistricts';
import { Toaster, toast } from "react-hot-toast";
import { FaFilter, FaSearch, FaMapMarkerAlt, FaCalendarAlt, FaTimes } from "react-icons/fa";
import { initializePayment } from "../utils/paymentUtils";
import PaymentModal from '../components/CashfreePaymentModal';
import { PaymentType } from '../utils/paymentTypes';
import { activateEvent, fetchEvents } from "../utils/api";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify"

export const tamilNaduDistricts = Object.keys(tamilNaduSubdistricts);

function CountdownTimer({ date, time }) {
  const [timeLeft, setTimeLeft] = useState("");
  useEffect(() => {
    if (!date || !time) {
      setTimeLeft("Invalid Date");
      return;
    }
    const targetDateTime = new Date(`${date}T${time}:00`);
    const interval = setInterval(() => {
      const now = new Date();
      const diff = targetDateTime - now;
      if (diff <= 0) {
        setTimeLeft("Event started!");
        clearInterval(interval);
      } else {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [date, time]);

  return <p className="text-xs text-green-600 font-semibold">Starts in: {timeLeft}</p>;
}

const Event = () => {

  const [activeTab, setActiveTab] = useState("All");
  const [filters, setFilters] = useState({
    name: "",
    location: "",
    startDate: "",
    mode: "",
  });
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    userType: "individual",
    founderId: "",
    startupName: "",
    industry: "",
    startupLocation: ""
  });
  const [processing, setProcessing] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [payLoading, setPayLoading] = useState(false)

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    founderId: "",
    startupName: "",
    industry: "",
    startupLocation: ""
  });
  const [page, setPage] = useState(1);

  const completedSyncedRef = useRef(new Set());

  // Helper: produce event DateTime (use event.time or fallback to midnight)
  const getEventDateTime = (event) => {
    try {
      const timePart = event.time ? event.time : "00:00";
      return new Date(`${event.date}T${timePart}:00`);
    } catch (e) {
      return new Date(event.date || 0);
    }
  };

  // Helper: compute a client-side status for an event based on datetime
  const computeStatusForEvent = (event) => {
    const eventDT = getEventDateTime(event);
    if (isNaN(eventDT.getTime())) return event.status || "Upcoming";
    return eventDT.getTime() < Date.now() ? "Completed" : (event.status || "Upcoming");
  };

  // Helper: normalize an array of events so UI uses computed status
  const normalizeEvents = (events) => {
    return events.map(e => {
      const computedStatus = computeStatusForEvent(e);
      return { ...e, status: computedStatus };
    });
  };

  const startupToken = localStorage.getItem("Startuptoken");
  const isLoggedIn = Boolean(startupToken && startupToken !== "DEMO_TOKEN");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["events", filters, activeTab, isLoggedIn, page],
    queryFn: () =>
      fetchEvents({
        ...filters,
        activeTab,
        page,
        limit: 7
      }),

    // 🔥 CACHE CONTROL
    staleTime: 0,                // data becomes stale immediately
    gcTime: 1000 * 60 * 10,        // cache kept for 2 minutes (v5)
    refetchOnWindowFocus: false,  // no auto refetch on tab switch
    refetchOnReconnect: false,    // no refetch on network reconnect
    retry: false,                // no retry spam
  });


  const {
    selectedDateEvents = [],
    upcomingEvents = [],
    hasDateFilter = false
  } = data || {};



  const normalizeModes = (event) => {
    if (Array.isArray(event?.eventMode)) return event.eventMode;
    if (typeof event?.eventMode === "string") return [event.eventMode];
    return [];
  };

  const filterByEventMode = (events = []) => {
    return events.filter(event => {
      // ✅ Always show completed events
      if (event.status === "Completed") {
        return true;
      }

      const modes = normalizeModes(event);

      if (isLoggedIn) {
        return modes.includes("Registered");
      }

      return modes.includes("NonRegistered");
    });
  };


  const finalSelectedDateEvents = filterByEventMode(selectedDateEvents);
  const finalUpcomingEvents = filterByEventMode(upcomingEvents);











  const handleFilterChange = e => {
    const { name, value } = e.target
    setFilters(prev => ({ ...prev, [name]: value }))
  }

  const clearFilters = () => {
    setFilters({ name: "", location: "", startDate: "", mode: "" })
  }

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.slice(0, maxLength).trimEnd() + "..."
      : text;
  };

  const handleUserTypeChange = (type) => {
    setForm(f => ({
      ...f,
      userType: type,
      ...(type === 'individual' ? {
        founderId: "",
        startupName: "",
        industry: "",
        startupLocation: "",
        isVerified: false
      } : {})
    }))
    setIsVerified(false)
  }

  const handleVerify = async () => {
    if (!form.founderId) {
      toast.error("Please enter a Founder ID to verify")
      return
    }

    try {
      setVerifyLoading(true)

      const resp = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/founder-kyc/${form.founderId}`
      )
      console.log("🔍 verify response:", resp.data)

      const { success, data } = resp.data
      if (!success) {
        throw new Error(resp.data.message || "Verification failed")
      }

      const { founderKyc, startup } = data
      if (!founderKyc?.founderId) {
        throw new Error("Founder not found")
      }

      setForm(f => ({
        ...f,
        founderId: founderKyc.founderId,
        name: founderKyc.founderName || f.name,
        email: founderKyc.email || f.email,
        phone: founderKyc.phoneNumber || f.phone,
        startupName: startup?.startupName || f.startupName,
        industry: startup?.industry || f.industry,
        startupLocation: startup?.location || founderKyc.address || f.startupLocation,
      }))

      setIsVerified(true)
      toast.success("Founder verified successfully!")
    } catch (err) {
      console.error("Verify error:", err)
      toast.error(
        err.response?.data?.message
        || err.message
        || "Verification failed"
      )
      setIsVerified(false)
    } finally {
      setVerifyLoading(false)
    }
  }

  const handleRegisterClick = (event) => {
    console.log(event)
    const isPaid = event?.type?.[0]?.toLowerCase() === "paid";
    const isFree = event?.type?.[0]?.toLowerCase() === "free";
    const token = localStorage.getItem("Startuptoken");

    if (event.eventMode == "NonRegistered") {
      setSelectedEvent(event)
      setShowModal(true)
      return
    }

    if ((isPaid || isFree) && !token) {
      toast.error("This event is only for Tactos Community Members please Login/register to continue");
      navigate("/login", { state: { from: location.pathname } });

      return;
    }

    setSelectedEvent(event)
    setShowModal(true)
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowModal(false)
    }
  }

  const handlePaymentSuccess = async (paymentResult) => {
    console.log("Clicked")
    try {
      const token = localStorage.getItem('Startuptoken');
      const orderId = paymentResult.orderId;


      console.log('Payment succeeded; verifying with backend…', orderId);
      toast.loading('Verifying payment...', { id: 'payment-verification' });

      // Verify payment with backend
      const verifyRes = await fetch('/api/payment/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ orderId })
      });

      if (!verifyRes.ok) {
        const errorData = await verifyRes.json();
        throw new Error(errorData.error || 'Payment verification failed');
      }

      const { verified } = await verifyRes.json();
      if (!verified) {
        throw new Error('Payment not verified');
      }

      // Complete the event registration
      await completeEventRegistration(paymentResult.transactionId);

      toast.success('Registration successful!', { id: 'payment-verification' });
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error(error.message || 'Payment verification failed', {
        id: 'payment-verification'
      });
    } finally {
      setProcessing(false);
      setShowPaymentModal(false);
      setPayLoading(false)
    }
  };

  const completeEventRegistration = async (transactionId = null) => {

    try {
      const token = localStorage.getItem('Startuptoken');

      // Restore registration data saved before payment
      const savedData = JSON.parse(sessionStorage.getItem("event_registration")) || {};

      // 🧩 Combine restored + current form safely
      const basePayload = {
        name: form?.name || savedData.name,
        email: form?.email || savedData.email,
        phone: form?.phone || savedData.phone,
        userType: form?.userType || savedData.userType || "individual",
        founderId: form?.founderId || savedData.founderId || null,
        startupName: form?.startupName || savedData.startupName || null,
        industry: form?.industry || savedData.industry || null,
        startupLocation: form?.startupLocation || savedData.startupLocation || null,
        eventId: selectedEvent?._id || savedData.eventId,
        eventName: selectedEvent?.name || savedData.eventName,
        eventType: selectedEvent?.type[0] || savedData.eventType,
        paymentStatus: transactionId ? "Paid" : "Unpaid",
        transactionId: transactionId || null,
      };

      // 🧠 Extract dynamic fields from event
      const eventFields = selectedEvent?.fields || savedData.fields || [];
      const dynamicFieldData = {};

      // Loop through dynamic fields and get user responses from `form`
      eventFields.forEach((field) => {
        const fieldKey = field.label; // using label as key
        dynamicFieldData[fieldKey] = form?.[fieldKey] || savedData[fieldKey] || "";
      });

      // Combine everything into final payload
      const payload = {
        ...basePayload,
        fields: eventFields.length > 0 ? eventFields.map(f => ({
          label: f.label,
          type: f.type,
          value: dynamicFieldData[f.label] || "",
          required: f.required || false
        })) : [],
      };


      // Include auth headers only if token exists
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      // 🔥 Send request
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/eventregister`,
        payload,
        config
      );

      // ✅ Check success
      if (res?.status === 201 && res?.data?.success) {
        await activateEvent(res.data.eventData.eventId, res.data.registrationId);
        toast.success("Registered successfully!");
        sessionStorage.removeItem("event_registration");
        setShowModal(false);
      } else {
        throw new Error(res?.data?.message || "Failed to complete registration");
      }

      return res.data;

    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.message || "Registration failed");
      throw error;
    }
  };



  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      userType: form.userType,
      founderId: "",
      startupName: "",
      industry: "",
      startupLocation: ""
    });
    setIsVerified(false);
    setProcessing(false);
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "name":
        if (!value.trim()) error = "Name is required";
        else if (value.length < 3) error = "Name must be at least 3 characters";
        else if (!/^[a-zA-Z\s]*$/.test(value)) error = "Name can only contain letters and spaces";
        break;
      case "email":
        if (!value.trim()) error = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = "Invalid email format";
        break;
      case "phone":
        if (!value.trim()) error = "Phone number is required";
        else if (!/^[0-9]{10}$/.test(value)) error = "Phone must be 10 digits";
        break;
      case "founderId":
        if (form.userType === "founder" && !isVerified && value.trim()) {
          error = "Please verify your founder ID";
        }
        break;
      case "startupName":
        if (form.userType === "founder" && !value.trim()) error = "Startup name is required";
        break;
      case "industry":
        if (form.userType === "founder" && !value.trim()) error = "Industry is required";
        break;
      case "startupLocation":
        if (form.userType === "founder" && !value.trim()) error = "Location is required";
        break;
      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};

    // 1. Validate dynamic fields
    selectedEvent?.fields?.forEach((field) => {
      const key = field.label.toLowerCase().replace(/\s+/g, "_");
      if (field.required && (!form[key] || !form[key].trim())) {
        newErrors[key] = `${field.label} is required`;
      }
    });

    // 2. Validate extra fields for Paid events if not in dynamic fields
    if (selectedEvent.type[0].toLowerCase() === "paid") {
      const requiredFields = ["name", "email", "phone"];
      requiredFields.forEach((field) => {
        const existsInFields = selectedEvent.fields.some(
          (f) => f.label.toLowerCase() === field
        );
        if (!existsInFields && (!form[field] || !form[field].trim())) {
          newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        }
      });
    }

    setErrors(newErrors);

    // Return true if no errors
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    console.log(form)
  }, [form])


  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone" && value && !/^[0-9]*$/.test(value)) {
      return;
    }

    if (name === "name" && value && !/^[a-zA-Z\s]*$/.test(value)) {
      return;
    }

    setForm(f => ({ ...f, [name]: value }));

    if (name in errors) {
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, value)
      }));
    }

    if (name === 'founderId' && isVerified) {
      setIsVerified(false);
    }
  };



  const handleSubmit = async (e) => {
    console.log("CLicked")
    e.preventDefault();
    console.log("selectedEvent", selectedEvent.type[0].toLowerCase())
    let token = localStorage.getItem('Startuptoken');
    const eventType = selectedEvent?.type?.[0]?.toLowerCase(); // "paid" or "free"
    const eventMode = selectedEvent?.eventMode[0];
    // if (selectedEvent?.type?.toLowerCase() === "paid" && !token) {
    //   toast.error("This event is only for Tactos Community Members please Login/register to continue");
    //   setProcessing(false);
    //   navigate("/login", { state: { from: location.pathname } });
    //   return;
    // }

    if (token === "DEMO_TOKEN") {
      token = null;
    }


    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    if (eventMode === "Registered" && !token) {

      toast.error("This event is for registered users only. Please login.");
      setProcessing(false);
      navigate("/login", { state: { from: location.pathname } });
      return;
    }





    try {
      // Store registration data in session storage
      sessionStorage.setItem(
        `event_registration`,
        JSON.stringify({
          ...form,
          eventId: selectedEvent._id,
          eventName: selectedEvent.name,
          eventType: selectedEvent.type[0],
          eventMode: selectedEvent.eventMode[0]
        })
      );

      const isPaid = eventType === "paid"

      console.log("type", form.userType)

      if (isPaid) {
        const token = localStorage.getItem('Startuptoken');
        // if (!token) {
        //   toast.error("Please log in to continue");
        //   setProcessing(false);
        //   return;
        // }

        // Convert amount to number and validate
        if (isPaid) {
          const amount = parseFloat(selectedEvent.amount);
          if (isNaN(amount) || amount <= 0) {
            throw new Error("Invalid event price");
          }
          // Initialize payment
          const paymentResult = await initializePayment({
            type: PaymentType.EVENT,
            amount,
            token,
            returnUrl: `${window.location.origin}/payment-callback`,
          });

          toast.loading("Redirecting to payment gateway...", { id: "payment" });
        }
      } else {
        await completeEventRegistration();
        // toast.success("Registration successful!");
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.message || 'Submission failed');
      setProcessing(false);
    } finally {
      setPayLoading(false)
    }
  };
  const handlePayButtonClick = () => {
    setPayLoading(true); // 🔥 start loader
    console.log("Pay");

    const isPaid = selectedEvent?.type?.[0]?.toLowerCase() === "paid";
    const eventType = selectedEvent?.eventMode?.[0]?.toLowerCase() === "registered";
    const token = localStorage.getItem("Startuptoken");

    if (isPaid && eventType && !token) {
      toast.error("Please log in to continue");
      setPayLoading(false);    // 🔥 stop loader
      setProcessing(false);
      setShowModal(false);
      navigate("/login", { state: { from: location.pathname } });
      return;
    }

    if (formRef.current) {
      formRef.current.requestSubmit(); // triggers handleSubmit
    }

    // Loader will stop inside handleSubmit after API finishes
  };


  // Helper to convert 24hr time to 12hr format with AM/PM
  const formatTime12hr = (timeStr) => {
    if (!timeStr) return "";
    const [hourStr, minStr] = timeStr.split(":");
    let hour = parseInt(hourStr, 10);
    const min = minStr || "00";
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${min} ${ampm}`;
  };

  function getOrdinal(n) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  function formatDateWithOrdinal(dateString) {
    if (!dateString) return "Date not mentioned";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Date not mentioned";

    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${day}${getOrdinal(day)} ${month}, ${year}`;
  }

    const getPlainText = (html) => {
  if (!html) return ""
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] })
}


  const renderEventCard = (event) => (
    <motion.div
      key={event._id}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-col md:flex-row">
        <div className="relative w-full md:w-40 h-40 md:h-[110px] flex-shrink-0 border-r border-gray-200 bg-white flex items-center justify-center overflow-hidden">
          {event.logo ? (
            <img
              src={event.logo}
              alt={event.name}
              className="max-w-none w-full h-full object-contain object-center"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
          )}
        </div>



        <div className="flex-1 px-4 py-2 relative">
          {activeTab != "Completed" && (
            <div className="absolute top-4 right-4 text-right">
              <span className="inline-block bg-[#fd3e3d] text-white text-xs font-bold px-3 py-1 rounded">
                {event.type[0] === "Free" ? "Free" : "Paid"}
              </span>
              <br />
              {event.type[0] === "Paid" && (
                <span className="inline-block mt-1 text-sm font-semibold text-gray-700">
                  ₹{event.amount || "TBA"}
                </span>
              )}
            </div>

          )}
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{event.name}</span>
            <span className="text-sm text-[#fd3e3d]">(Hosted by {event.title || "TBA"})</span>
            {event.date && event.time && (
              <span className="">
                <CountdownTimer date={event.date} time={event.time} />
              </span>
            )}
          </h3>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700 mb-2 px-3 rounded-lg">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4 text-[#fd3e3d]" />
              <span>{event.location || event.address}</span>
            </span>

            <span className="flex items-center gap-1 px-2 py-1 rounded">
              <Calendar className="h-4 w-4 text-[#fd3e3d]" />
              <span>{formatDateWithOrdinal(event.date)}</span>
            </span>

            {event.time && (
              <span className="flex items-center gap-1 px-2 py-1 rounded">
                <Clock className="h-4 w-4 text-yellow-600" />
                <span>{formatTime12hr(event.time)}</span>
              </span>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <p className="text-sm text-gray-700 md:max-w-[60%]">
              {truncateText(getPlainText(event.description), 80)}
            </p>

             {/* <div className="prose prose-slate prose-lg max-w-none">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(event.description),
                                }}
                              />
            
                            </div> */}



            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/event-description/${event._id}`)}
                className="inline-flex items-center gap-2 bg-[#020c1b] hover:bg-[#0d1a2e] text-white text-xs md:text-sm font-semibold py-2 px-4 rounded-full transition duration-200"
              >
                View Details
              </button>
              {activeTab != "Completed" && (
                <button
                  onClick={() => handleRegisterClick(event)}
                  className="inline-flex items-center gap-2 bg-[#020c1b] hover:bg-[#0d1a2e] text-white text-xs md:text-sm font-semibold py-2 px-4 rounded-full transition duration-200"
                  disabled={event.type[0] === "Paid" && (!event.amount || isNaN(parseFloat(event.amount)))}
                >
                  Register
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderSection = (events, title) => {
    if (events.length === 0) return null;

    return (
      <>
        {title && <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>}
        <div className="space-y-4">
          {events.map(event => renderEventCard(event))}
        </div>
      </>
    );
  };

  const renderEventCards = () => {
    if (isLoading || isFetching) {
      return (
        <div className="flex justify-center items-center h-40">
          <img
            src="/tactos.f012ee2d7d02548b905c.png"
            alt="Loading"
            className="w-12 h-12 object-contain animate-pulse"
          />
        </div>
      );
    }

    const totalEvents =
      finalSelectedDateEvents.length + finalUpcomingEvents.length;

    if (hasDateFilter) {
      return (
        <>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {totalEvents} Events
            </h2>
          </div>

          {finalSelectedDateEvents.length > 0 ? (
            renderSection(
              finalSelectedDateEvents,
              `Events on ${formatDateWithOrdinal(filters.startDate)}`
            )
          ) : (
            <div className="text-center py-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No events on {formatDateWithOrdinal(filters.startDate)}
              </h3>
            </div>
          )}

          {finalUpcomingEvents.length > 0 &&
            renderSection(finalUpcomingEvents, "Upcoming Events")}

          {totalEvents === 0 && (
            <div className="text-center py-16 rounded-lg">
              <CalendarIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900">
                No events found
              </h3>
            </div>
          )}
        </>
      );
    }

    // No date filter
    return (
      <>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {finalUpcomingEvents.length} Events
          </h2>
        </div>

        {finalUpcomingEvents.length > 0 ? (
          <div className="space-y-4">
            {finalUpcomingEvents.map(event =>
              renderEventCard(event)
            )}
          </div>
        ) : (
          <div className="text-center py-16 rounded-lg">
            <CalendarIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900">
              No events found
            </h3>
          </div>
        )}
        {data?.pagination?.totalPages > 1 && (
          <div className="flex justify-center items-center gap-6 mt-10">

            {/* PREV */}
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className={`
        group relative overflow-hidden
        px-6 py-2 rounded-full
        font-semibold tracking-wide
        border transition-all duration-300 ease-out
        active:scale-95
        ${page === 1
                  ? "border-gray-300 text-gray-400 cursor-not-allowed"
                  : "border-black text-black hover:text-white"
                }
      `}
            >
              <span className="relative z-10 flex items-center gap-2">
                ← Prev
              </span>

              {/* hover bg */}
              {page !== 1 && (
                <span className="
          absolute inset-0
          bg-black
          translate-x-[-100%]
          group-hover:translate-x-0
          transition-transform duration-300 ease-out
        " />
              )}
            </button>

            {/* PAGE INDICATOR */}
            <div className="
      px-7 py-2 rounded-full
      bg-[#fd3e3d] text-white
      font-bold tracking-wide
      shadow-md
      animate-pulseSlow
    ">
              Page {data.pagination.currentPage}
              <span className="opacity-80"> / {data.pagination.totalPages}</span>
            </div>

            {/* NEXT */}
            <button
              disabled={page === data.pagination.totalPages}
              onClick={() => setPage(p => p + 1)}
              className={`
        group relative overflow-hidden
        px-6 py-2 rounded-full
        font-semibold tracking-wide
        border transition-all duration-300 ease-out
        active:scale-95
        ${page === data.pagination.totalPages
                  ? "border-gray-300 text-gray-400 cursor-not-allowed"
                  : "border-black text-black hover:text-white"
                }
      `}
            >
              <span className="relative z-10 flex items-center gap-2">
                Next →
              </span>

              {/* hover bg */}
              {page !== data.pagination.totalPages && (
                <span className="
          absolute inset-0
          bg-black
          translate-x-[100%]
          group-hover:translate-x-0
          transition-transform duration-300 ease-out
        " />
              )}
            </button>

          </div>
        )}



      </>
    );
  };


  return (
    <div className="relative">
      <button
        onClick={() => setShowMobileFilter(!showMobileFilter)}
        className="lg:hidden fixed bottom-6 right-6 z-50 bg-blue-600 text-white p-3 rounded-full shadow-lg"
      >
        {showMobileFilter ? <X className="w-5 h-5" /> : <Filter className="w-5 h-5" />}
      </button>

      <div className="container lg:sticky lg:top-[67px] z-40 border-b border-gray-200 bg-[#f8f6f5]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto pb-2 scrollbar-hide">
            {["All", "Free", "Paid", "Exclusive", "Completed"].map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab),
                    setPage(1)
                }}
                className={`whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === tab
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                {tab === "All" ? "All Events" :
                  tab === "Free" ? "Free Events" :
                    tab === "Paid" ? "Paid Events" :
                      tab === "Exclusive" ? "Exclusive Events" :
                        "Completed Events"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className={`${showMobileFilter ? "block" : "hidden"} lg:block w-full lg:w-1/3 xl:w-1/4`}>
            <div className="lg:sticky lg:top-40 border border-gray-200/50 rounded-3xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent flex items-center gap-3">
                  <FaFilter className="text-blue-500 text-lg" />
                  Filters
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 rounded-xl font-semibold text-gray-700 transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => setShowMobileFilter(false)}
                    className="lg:hidden p-2 bg-white/80 backdrop-blur-sm hover:bg-white rounded-xl transition-all duration-200 shadow-lg"
                  >
                    <FaTimes className="text-gray-600" />
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <FaSearch className="text-blue-500 text-sm" />
                    Search Events
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={filters.name}
                      onChange={handleFilterChange}
                      placeholder="Search by name..."
                      className="w-full px-4 py-3 pl-12 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:shadow-lg text-sm"
                    />
                    <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <FaCalendarAlt className="text-purple-500 text-sm" />
                    Mode
                  </label>
                  <select
                    name="mode"
                    value={filters.mode}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:shadow-lg appearance-none cursor-pointer text-sm"
                  >
                    <option value="">Any Mode</option>
                    <option value="Physical">Physical</option>
                    <option value="Online">Online</option>
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <FaMapMarkerAlt className="text-red-500 text-sm" />
                    District
                  </label>
                  <select
                    name="location"
                    value={filters.location}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:shadow-lg appearance-none cursor-pointer text-sm"
                  >
                    <option value="">All Districts</option>
                    {tamilNaduDistricts.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <FaCalendarAlt className="text-green-500 text-sm" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:shadow-lg cursor-pointer text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderEventCards()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Registration Modal */}
      <AnimatePresence>
        {showModal && selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={handleBackdropClick}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="
                bg-white rounded-lg p-6 w-full
                max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg
                mx-auto
                max-h-[90vh] overflow-y-auto
                relative
              "
            >
              <button
                onClick={() => {
                  setShowModal(false);
                  setIsVerified(false);
                }}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-xl"
              >
                &times;
              </button>
              <h3 className="text-xl font-semibold text-gray-800 text-center mb-4">
                {selectedEvent.type[0].toLowerCase() === "paid"
                  ? "Pay & Register"
                  : "Register"}
              </h3>

              <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <button
                    type="button"
                    onClick={() => handleUserTypeChange('individual')}
                    className={`px-4 py-2 rounded-lg ${form.userType === 'individual' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Applicant
                  </button>
                  {/* <button
                    type="button"
                    onClick={() => handleUserTypeChange('founder')}
                    className={`px-4 py-2 rounded-lg ${form.userType === 'founder' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Startup Founder
                  </button> */}
                </div>

                <div className="space-y-4">

                  {/* Add email & phone fields if paid event and not already present */}

                  {selectedEvent.type[0] === "Paid" &&
                    !selectedEvent.fields.some(
                      (f) => f.label.toLowerCase() === "email"
                    ) && (
                      <div className="mb-3">
                        <input
                          type="text"
                          name="name"
                          placeholder="Name *"
                          required
                          value={form.name || ""}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        {errors.email && (
                          <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                        )}
                      </div>
                    )}
                  {selectedEvent.type[0] === "Paid" &&
                    !selectedEvent.fields.some(
                      (f) => f.label.toLowerCase() === "email"
                    ) && (
                      <div className="mb-3">
                        <input
                          type="email"
                          name="email"
                          placeholder="Email *"
                          required
                          value={form.email || ""}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        {errors.email && (
                          <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                        )}
                      </div>
                    )}

                  {selectedEvent.type[0] === "Paid" &&
                    !selectedEvent.fields.some(
                      (f) => f.label.toLowerCase() === "phone"
                    ) && (
                      <div className="mb-3">
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Phone *"
                          required
                          value={form.phone || ""}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        {errors.phone && (
                          <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                        )}
                      </div>
                    )}

                  {selectedEvent?.fields?.length > 0 &&
                    selectedEvent.fields.map((each) => {
                      const fieldName = each.label.toLowerCase().replace(/\s+/g, "_");

                      // Determine if this field should be required due to paid event
                      const isPaidRequired =
                        selectedEvent.type === "paid" &&
                        (fieldName === "email" || fieldName === "phone");

                      return (
                        <div key={fieldName} className="mb-3">
                          <input
                            type={each.value}
                            name={fieldName}
                            placeholder={each.label}
                            required={each.required || isPaidRequired}
                            value={form[fieldName] || ""}
                            onChange={(e) =>
                              setForm({ ...form, [fieldName]: e.target.value })
                            }
                            className={`w-full p-3 border ${errors[fieldName] ? "border-red-500" : "border-gray-200"
                              } rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400`}
                          />
                          {errors[fieldName] && (
                            <p className="text-red-500 text-xs mt-1">{errors[fieldName]}</p>
                          )}
                        </div>
                      );
                    })}

                  {selectedEvent?.fields?.length === 0 && selectedEvent?.type[0] !== "paid" && (
                    <div className="text-center py-6">
                      <p className="text-gray-700 font-medium">
                        No additional details are required for this event.
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        You can proceed and apply directly.
                      </p>
                    </div>
                  )}




                </div>

                {form.userType === 'founder' && (
                  <div className="space-y-4">
                    <div className="flex items-end space-x-2">
                      <div className="flex-1 flex flex-col">
                        <label htmlFor="founderId" className="text-gray-700 font-medium mb-1">
                          Founder ID
                        </label>
                        <input
                          id="founderId"
                          name="founderId"
                          type="text"
                          placeholder="Enter Founder ID (optional)"
                          value={form.founderId}
                          onChange={handleChange}
                          className={`w-full p-3 border ${errors.founderId ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400`}
                        />
                        {errors.founderId && <p className="text-red-500 text-xs mt-1">{errors.founderId}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={handleVerify}
                        disabled={verifyLoading || !form.founderId || isVerified}
                        className="px-4 py-2 mb-2 bg-indigo-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                      >
                        {verifyLoading ? 'Verifying...' : isVerified ? 'Verified' : 'Verify'}
                      </button>
                    </div>

                    {isVerified && (
                      <div className="bg-green-50 p-3 rounded-lg text-green-700 text-sm">
                        Founder verified successfully
                      </div>
                    )}

                    <div>
                      <input
                        name="startupName"
                        type="text"
                        placeholder="Startup Name *"
                        value={form.startupName}
                        onChange={handleChange}
                        required
                        className={`w-full p-3 border ${errors.startupName ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400`}
                      />
                      {errors.startupName && <p className="text-red-500 text-xs mt-1">{errors.startupName}</p>}
                    </div>
                    <div>
                      <input
                        name="industry"
                        type="text"
                        placeholder="Industry *"
                        value={form.industry}
                        onChange={handleChange}
                        required
                        className={`w-full p-3 border ${errors.industry ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400`}
                      />
                      {errors.industry && <p className="text-red-500 text-xs mt-1">{errors.industry}</p>}
                    </div>
                    <div>
                      <input
                        name="startupLocation"
                        type="text"
                        placeholder="Startup Location *"
                        value={form.startupLocation}
                        onChange={handleChange}
                        required
                        className={`w-full p-3 border ${errors.startupLocation ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400`}
                      />
                      {errors.startupLocation && <p className="text-red-500 text-xs mt-1">{errors.startupLocation}</p>}
                    </div>
                  </div>
                )}

                {selectedEvent.type?.[0]?.toLowerCase() === "paid" && (
                  <p className="text-sm text-gray-500 text-center">
                    You'll be redirected to a secure payment page to complete your registration.
                  </p>
                )}
                <button
                  type="button"
                  onClick={handlePayButtonClick}
                  disabled={processing || payLoading}
                  className="w-full py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white font-medium rounded-lg hover:opacity-90 transition disabled:opacity-70 flex items-center justify-center"
                >
                  {processing ? (
                    <ClipLoader color="#ffffff" size={20} className="mr-2" />
                  ) : selectedEvent.type[0].toLowerCase() === "paid" ? (
                    processing ? (
                      "Loading..."
                    ) : (
                      `Pay ₹${selectedEvent.amount}`
                    )
                  ) : (
                    "Submit"
                  )}
                </button>

              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {showPaymentModal && (
        <PaymentModal
          paymentSessionId={paymentSessionId}
          orderId={currentOrderId}
          onSuccess={handlePaymentSuccess}
          onClose={() => {
            setShowPaymentModal(false);
            setProcessing(false);
          }}
          onFailure={(error) => {
            toast.error(error.message || 'Payment failed');
            setProcessing(false);
          }}
        />
      )}
    </div>
  );
}

export default Event;